import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from 'semantic-ui-react';
import { Field, useForm } from 'react-final-form';

// @ts-ignore
import validators, { composeValidators, maxLength20 } from 'forms/validators';
import { RenderField, normalizers, RenderCheckbox } from 'forms';
import SprayMethodDropdown from 'components/SprayMethodDropdown';
import SprayOperatorsDropdown from 'components/SprayOperatorsDropdown';
import WaterSourceDropdown from 'components/WaterSourceDropdown';
import ChangeFieldValueBasedOnAnotherFieldValue from 'components/ChangeFieldValueBasedOnAnotherFieldValue';
import SprayEventTypeCropGate from 'components/SprayEventTypeCropGate';
import {
    SprayEventStartingRow,
    SprayEventOperatorsIndex
} from 'services/sprayEvents/types';
import FieldValues from 'components/FieldValues';
import get from 'lodash.get';
import { makeSelectSprayEventMultipleOperatorsTabActiveIndex } from 'services/sprayEventMultipleOperatorsTab/selectors';
import { setSprayEventMultipleOperatorsTabIndex } from 'services/sprayEventMultipleOperatorsTab/actions';
import {
    is2DApplicationType,
    isMultiOperatorApplicationType
} from 'services/sprayEvents/utils';
import WaterTotalField from './components/WaterTotalField';
import SprayJustificationDropdown from './components/SprayJustificationDropdown';
import ApplicationRateDropdown from './components/ApplicationRateDropdown';
import ApplicationTypeDropdown from './components/ApplicationTypeDropdown';
import MultipleApplicatorsTab from '../MultipleOperatorsTab';
import { OperatorFieldNames } from '../MultipleOperatorsTab/constants';

interface Props {
    lastSubmittedAt?: Date;
}
const { toNull } = normalizers;
const { isRequired } = validators;

export default function ApplicationPage(props: Props): React.ReactElement {
    const { lastSubmittedAt } = props;
    const form = useForm();
    const dispatch = useDispatch();

    const getFieldName = (name: string, fieldName: string): string =>
        name.length > 0 ? `${name}.${fieldName}` : fieldName;

    const tabActiveIndex = useSelector(
        makeSelectSprayEventMultipleOperatorsTabActiveIndex()
    );

    React.useEffect(() => {
        return () => {
            dispatch(setSprayEventMultipleOperatorsTabIndex(0));
        };
    });

    const setOperatorDirection = (applicationType: string) => {
        if (isMultiOperatorApplicationType(applicationType)) {
            form.change(
                getFieldName(OperatorFieldNames.OUTSIDE, 'startingRow'),
                SprayEventStartingRow.OUTSIDE
            );

            form.change(
                getFieldName(OperatorFieldNames.DOUBLE, 'startingRow'),
                SprayEventStartingRow.DOUBLE
            );
        } else {
            form.change(
                getFieldName(OperatorFieldNames.STANDARD, 'startingRow'),
                SprayEventStartingRow.ANY
            );
        }
    };

    return (
        <Grid className="spray-event-modal-step">
            <ChangeFieldValueBasedOnAnotherFieldValue
                fieldToBeListened="sprayJustificationOption"
                fieldToBeChanged="justification"
            />
            <Grid.Row>
                <Grid.Column width="8">
                    <h4>Application details</h4>
                </Grid.Column>
            </Grid.Row>

            <SprayEventTypeCropGate>
                <Grid.Row>
                    <Grid.Column>
                        <ApplicationTypeDropdown
                            name="applicationType"
                            validate={isRequired}
                            placeholder="Please choose application Type"
                        />
                    </Grid.Column>
                </Grid.Row>
            </SprayEventTypeCropGate>

            <FieldValues
                fieldNames={['applicationType', 'sprayEventOperations']}
            >
                {({
                    applicationType,
                    sprayEventOperations
                }): React.ReactElement => {
                    setOperatorDirection(applicationType);

                    if (isMultiOperatorApplicationType(applicationType)) {
                        const outSideRowOperator = get(
                            sprayEventOperations,
                            SprayEventOperatorsIndex.OUTSIDE,
                            undefined
                        );
                        const doubleRowOperator = get(
                            sprayEventOperations,
                            SprayEventOperatorsIndex.DOUBLE,
                            undefined
                        );

                        const excludeOutSideRowOperatorId =
                            outSideRowOperator &&
                            outSideRowOperator.operatorPersonId;
                        const excludeDoubleRowOperatorId =
                            doubleRowOperator &&
                            doubleRowOperator.operatorPersonId;

                        return (
                            <MultipleApplicatorsTab
                                tabActiveIndex={tabActiveIndex}
                                lastSubmittedAt={lastSubmittedAt}
                                childForOutsideRowTab={
                                    <SprayOperatorsDropdown
                                        label="Applicator"
                                        name={getFieldName(
                                            OperatorFieldNames.OUTSIDE,
                                            'operatorPersonId'
                                        )}
                                        validate={isRequired}
                                        placeholder="Add an applicator for outside row"
                                        excludeApplicatorId={
                                            excludeDoubleRowOperatorId
                                        }
                                    />
                                }
                                childForDoubleRowTab={
                                    <SprayOperatorsDropdown
                                        label="Applicator"
                                        name={getFieldName(
                                            OperatorFieldNames.DOUBLE,
                                            'operatorPersonId'
                                        )}
                                        validate={isRequired}
                                        placeholder="Add an applicator for double row"
                                        excludeApplicatorId={
                                            excludeOutSideRowOperatorId
                                        }
                                    />
                                }
                            />
                        );
                    }

                    return (
                        <React.Fragment>
                            {is2DApplicationType(applicationType) && (
                                <Grid.Row>
                                    <Grid.Column>
                                        <Field
                                            component={RenderField as any}
                                            name={getFieldName(
                                                OperatorFieldNames.TWODIMENSION,
                                                'twoDimensionStartingRow'
                                            )}
                                            label="Starting Row"
                                            nullOnUnmount={false}
                                            parse={toNull}
                                            validate={composeValidators(
                                                isRequired,
                                                maxLength20
                                            )}
                                        />
                                    </Grid.Column>
                                </Grid.Row>
                            )}
                            <Grid.Row>
                                <Grid.Column>
                                    <SprayOperatorsDropdown
                                        label="Applicator"
                                        name={getFieldName(
                                            OperatorFieldNames.STANDARD,
                                            'operatorPersonId'
                                        )}
                                        validate={isRequired}
                                        placeholder="Add an applicator"
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </React.Fragment>
                    );
                }}
            </FieldValues>

            <SprayEventTypeCropGate
                fallback={
                    <Grid.Row>
                        <Grid.Column width="8">
                            <Field
                                component={RenderField as any}
                                name="technicalPersonName"
                                label="Technically responsible person"
                                nullOnUnmount={false}
                                parse={toNull}
                                validate={isRequired}
                            />
                        </Grid.Column>
                        <Grid.Column width="8">
                            <ApplicationRateDropdown
                                name="sprayApplicationRate"
                                validate={isRequired}
                            />
                        </Grid.Column>
                    </Grid.Row>
                }
            >
                <React.Fragment>
                    <Grid.Row>
                        <Grid.Column width="16">
                            <Field
                                component={RenderField as any}
                                name="technicalPersonName"
                                label="Technically responsible person"
                                nullOnUnmount={false}
                                parse={toNull}
                                validate={isRequired}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width="8">
                            <ApplicationRateDropdown
                                name="sprayApplicationRate"
                                validate={isRequired}
                            />
                        </Grid.Column>
                        <Grid.Column width="8">
                            <FieldValues
                                fieldNames={[
                                    'sprayApplicationRate',
                                    'sprayEntityIds',
                                    'applicationType'
                                ]}
                            >
                                {({
                                    sprayApplicationRate: applicationRate,
                                    sprayEntityIds: entityIds,
                                    applicationType
                                }): React.ReactElement => {
                                    return (
                                        <WaterTotalField
                                            waterRate={applicationRate}
                                            entityIds={entityIds}
                                            applicationType={applicationType}
                                        />
                                    );
                                }}
                            </FieldValues>
                        </Grid.Column>
                    </Grid.Row>
                </React.Fragment>
            </SprayEventTypeCropGate>
            <Grid.Row>
                <Grid.Column width="8">
                    <SprayMethodDropdown
                        name="sprayMethod"
                        validate={isRequired}
                        placeholder="Add a spray method"
                    />
                </Grid.Column>
                <Grid.Column width="8">
                    <WaterSourceDropdown
                        name="sprayWaterSourceId"
                        validate={isRequired}
                        placeholder="Add a water source"
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width="8">
                    <SprayJustificationDropdown
                        name="sprayJustificationOption"
                        placeholder="Add a default justification"
                    />
                </Grid.Column>
                <Grid.Column width="8">
                    <Field
                        component={RenderField as any}
                        name="justification"
                        label="Justification (editable)"
                        nullOnUnmount={false}
                        parse={toNull}
                        validate={isRequired}
                    />
                </Grid.Column>
            </Grid.Row>
            <SprayEventTypeCropGate>
                <Grid.Row>
                    <Grid.Column>
                        <Field
                            component={RenderCheckbox as any}
                            name="isFinalSpray"
                            label="Final spray"
                            nullOnUnmount={false}
                            parse={(value): boolean => Boolean(value)}
                            type="checkbox"
                        />
                    </Grid.Column>
                </Grid.Row>
            </SprayEventTypeCropGate>
        </Grid>
    );
}
